import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Sheet } from 'components/ds';
import styles from './ReportSheet.module.css';

/** 신고 사유 — 서버(report.reason_code)와 같은 값입니다 */
export const REPORT_REASON_CODES = [
    'INAPPROPRIATE', 'NSFW', 'SPAM', 'PRIVACY', 'ETC',
] as const;

export type ReportReasonCode = typeof REPORT_REASON_CODES[number];

/** 사유 코드 → 문구 키. 모임 글 신고와 같은 문구를 씁니다 */
const LABEL_KEY: Record<ReportReasonCode, string> = {
    INAPPROPRIATE: 'posts.reasonInappropriate',
    NSFW: 'posts.reasonNsfw',
    SPAM: 'posts.reasonSpam',
    PRIVACY: 'posts.reasonPrivacy',
    ETC: 'posts.reasonEtc',
};

/**
 * 신고 시트.
 *
 * 사유를 고르고 자세한 내용을 덧붙여 냅니다. 무엇을 신고하는지는 부르는 쪽이
 * 압니다 — 이 시트는 사유만 받아 넘깁니다.
 *
 * 문구는 모임 글 신고(MeetingPostsSection)와 같은 키를 씁니다. 화면마다 말이
 * 다르면 같은 기능으로 읽히지 않습니다.
 */
export const ReportSheet: React.FC<{
    open: boolean;
    onClose: () => void;
    onSubmit: (reasonCode: ReportReasonCode, reasonText: string) => void;
    submitting?: boolean;
}> = ({ open, onClose, onSubmit, submitting }) => {
    const { t } = useTranslation();
    const [code, setCode] = useState<ReportReasonCode>('INAPPROPRIATE');
    const [text, setText] = useState('');

    return (
        <Sheet open={open} onClose={onClose} title={t('posts.reportTitle')} closeLabel={t('posts.cancel')}>
            <p className={styles.lead}>{t('posts.reportSelectReason')}</p>
            <div className={styles.reasons}>
                {REPORT_REASON_CODES.map(c => (
                    <label key={c} className={code === c ? `${styles.reason} ${styles.reasonOn}` : styles.reason}>
                        <input
                            type="radio"
                            name="report-reason"
                            value={c}
                            checked={code === c}
                            onChange={() => setCode(c)}
                        />
                        <span className={styles.mark} aria-hidden="true" />
                        {t(LABEL_KEY[c])}
                    </label>
                ))}
            </div>
            <textarea
                className={styles.textarea}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('posts.reportDetailPlaceholder')}
                rows={3}
                maxLength={1000}
            />
            <p className={styles.notice}>{t('posts.reportNotice')}</p>
            <div className={styles.actions}>
                <Button variant="secondary" block onClick={onClose}>{t('posts.cancel')}</Button>
                <Button variant="danger" block disabled={submitting}
                        onClick={() => onSubmit(code, text.trim())}>
                    {t('posts.reportTitle')}
                </Button>
            </div>
        </Sheet>
    );
};
